import { useLingui } from "@lingui/react/macro";
import { Badge } from "@offstreetparking/design-system/dataDisplay/Badge";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { ParkingIcon } from "@offstreetparking/design-system/icons/ParkingIcon";
import { useLocale } from "@offstreetparking/design-system/providers";
import type { WizardParkingChoice } from "../../../../PermitContextProvider/types";

export type ParkingChoiceTagProps = {
	choice: WizardParkingChoice;
	timezone: string;
};

export const ParkingChoiceTag = ({ choice, timezone }: ParkingChoiceTagProps) => {
	const { t } = useLingui();
	const { locale } = useLocale();

	if (choice.availability === "outside-recurring-window" && choice.recurringWindowStartsAt) {
		const dayStr = formatDate({
			date: choice.recurringWindowStartsAt,
			formatType: "Weekday",
			timezone,
			language: locale,
		});
		const timeStr = formatDate({
			date: choice.recurringWindowStartsAt,
			formatType: "Time",
			timezone,
			language: locale,
		});
		return (
			<Badge variant="success" size="sm" icon={ParkingIcon}>
				{t`Registration starts ${dayStr} at ${timeStr}`}
			</Badge>
		);
	}

	if (!choice.rangeStart || !choice.rangeEnd) {
		return null;
	}

	const isUpcoming = choice.rangeStart && new Date(choice.rangeStart) > new Date();

	const date = isUpcoming ? choice.rangeStart : choice.rangeEnd;
	const intervals = choice.time.intervals ?? [];
	const lastInterval = intervals[intervals.length - 1];

	if (!isUpcoming && lastInterval && lastInterval.endDateTimeISO === choice.rangeEnd) {
		return null;
	}

	const dateStr = formatDate({ date, formatType: "Abbreviated_Date", timezone, language: locale });
	const timeStr = formatDate({ date, formatType: "Time", timezone, language: locale });

	return (
		<Badge variant={isUpcoming ? "success" : "warning"} size="sm">
			{isUpcoming
				? t`Option available ${dateStr} at ${timeStr}`
				: t`Option available until ${dateStr} at ${timeStr}`}
		</Badge>
	);
};
